import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Package } from 'lucide-react'

import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/ui/item'
import { cn } from '@/lib/utils'
import { AX_PRODUCTS_QUERY_KEY, AxProductsApi } from '@/services/earnings/axProductsApi'
import { ProductPickerWizard } from './ProductPickerWizard'

export { AX_PRODUCTS_QUERY_KEY }

export interface MarketplaceProductSelectProps {
  /** Product id, or '' when the line item is not booked against a product. */
  value: string
  onChange: (productId: string) => void
  /** Open the picker straight away — for a line item that is being created. */
  defaultOpen?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Shows the marketplace (programmatic) product a line item is booked against;
 * clicking it opens the product wizard to pick another.
 */
export function MarketplaceProductSelect({
  value,
  onChange,
  defaultOpen = false,
  disabled,
  className,
}: MarketplaceProductSelectProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(defaultOpen && !disabled)

  const { data: products = [] } = useQuery({
    queryKey: AX_PRODUCTS_QUERY_KEY,
    queryFn: () => AxProductsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })

  const selected = products.find((p) => p.id === value)

  return (
    <>
      <Item
        asChild
        size="sm"
        variant="outline"
        className={cn('min-w-0 bg-card shadow-xs hover:bg-accent/50', className)}
      >
        <button type="button" disabled={disabled} onClick={() => setOpen(true)}>
          <ItemMedia
            variant="icon"
            className="border-transparent bg-(--ad-primary-soft) text-(--ad-color-1)"
          >
            <Package />
          </ItemMedia>
          <ItemContent className="min-w-0 text-left">
            <ItemTitle className={cn('truncate', !selected && 'text-muted-foreground')}>
              {selected?.name ?? (value ? value : t('lineItems.selectProduct'))}
            </ItemTitle>
            {selected && (
              <ItemDescription className="truncate">
                {[t(`products.type_${selected.type}`), ...selected.labels].join(' · ')}
              </ItemDescription>
            )}
          </ItemContent>
        </button>
      </Item>
      <ProductPickerWizard open={open} onOpenChange={setOpen} value={value} onSelect={onChange} />
    </>
  )
}
